import React, { useMemo, useState } from "react";

import { snakeCase } from "lodash";

import { productAnalytics, useNavigate, useTranslation, useUserContext } from "utils";

import { OnboardingPracticeKey } from "consts";

import { AlertBanner, Message, Typography } from "components";

import { CropRotationTable } from "pages/app/scenario-analysis/components/crop-rotation/CropRotationTable";
import { useMainCropOptions } from "pages/app/scenario-analysis/hooks/useMainCropOptions";
import { CropYear } from "pages/app/scenario-analysis/types/scenario";
import { classifyLift, getScenarioUpliftPercent } from "pages/app/scenario-analysis/utils/uplift";
import { useScenarioOnboarding } from "pages/app/scenario-onboarding/ScenarioOnboardingContext";
import { OnboardingLayout } from "pages/app/scenario-onboarding/components/OnboardingLayout";
import { PracticeToggleCard } from "pages/app/scenario-onboarding/components/PracticeToggleCard";
import { useChangeScenarioRecalc } from "pages/app/scenario-onboarding/hooks/useChangeScenarioRecalc";
import { useUpdateSoilSightOnboardingSetting } from "pages/app/scenario-onboarding/hooks/useSoilSightOnboardingSetting";
import { useStableGeneratedChangeScenario } from "pages/app/scenario-onboarding/hooks/useStableGeneratedChangeScenario";
import {
  FIELD_BY_PRACTICE,
  applyPracticeToRows,
  countChangedPractices,
  getAppliedPractices,
  getHighlightedFieldsByYearId,
  isPracticeInRows,
  isPracticeToggleInert,
} from "pages/app/scenario-onboarding/utils/recommendationState";

const PRACTICE_KEYS = Object.keys(FIELD_BY_PRACTICE) as OnboardingPracticeKey[];

export const Step3ImprovedScenarioPage = () => {
  const { t } = useTranslation(["scenarioAnalysis", "global"]);
  const navigate = useNavigate();
  const { reloadUserData } = useUserContext();
  const { cropYears, changeCropYears, setChangeCropYears, scenarioRefs, outcomes, calcStatus } = useScenarioOnboarding();
  const updateOnboardingSetting = useUpdateSoilSightOnboardingSetting();
  const [isCompleting, setIsCompleting] = useState(false);

  useChangeScenarioRecalc();

  // The last step — finishing is the ONE moment a user becomes onboarded.
  const handleFinish = async () => {
    setIsCompleting(true);
    try {
      await updateOnboardingSetting.mutateAsync({ status: "completed" });
      productAnalytics.track("soilsights_onboarding:flow_complete", {
        lift_classification: outcomes ? snakeCase(liftClassification) : null,
        practices_changed_count: countChangedPractices(cropYears, changeCropYears),
      });
      await reloadUserData().catch(() => {});
      navigate("/scenarios");
    } catch {
      Message({ content: t("scenarioAnalysis:onboarding.submitError"), icon: "error" });
    } finally {
      setIsCompleting(false);
    }
  };

  const { practiceCrops, mainCropOptions, isPracticeCropsFetching } = useMainCropOptions(changeCropYears);

  const generated = useStableGeneratedChangeScenario(cropYears, practiceCrops, new Date().getFullYear());

  // Without the catalogue the generator has no residue flags or sowing calendars and silently degrades,
  // so the switches stay locked until it lands rather than writing baseline values over the rotation.
  const isCatalogueMissing = !practiceCrops;

  // The switches read the rows, so editing a practice in the table below flips its switch.
  const practiceToggles = useMemo(() => getAppliedPractices(generated, changeCropYears), [generated, changeCropYears]);

  // Highlights follow the rows, so a manual edit drops one and switching the practice back on brings it back.
  const highlightedFields = useMemo(
    () => getHighlightedFieldsByYearId(generated.changeYears, cropYears, changeCropYears),
    [generated, cropYears, changeCropYears]
  );

  const isCalculating = calcStatus === "calculating";
  const [isLowLiftBannerDismissed, setIsLowLiftBannerDismissed] = useState(false);

  const upliftPct = getScenarioUpliftPercent(outcomes, scenarioRefs.baselineScenarioId, scenarioRefs.changeScenarioId);
  const liftClassification = classifyLift(upliftPct);

  const handleToggle = (practiceKey: OnboardingPracticeKey, enabled: boolean) => {
    productAnalytics.track("soilsights_onboarding:practice_update", { practice: snakeCase(practiceKey), is_enabled: enabled });
    // On restores the generated value, off the baseline one; other practices' edits survive either way.
    setChangeCropYears((previous) => applyPracticeToRows(previous, enabled ? generated.changeYears : cropYears, FIELD_BY_PRACTICE[practiceKey]));
  };

  const handleYearDataChange = (yearId: string, updates: Partial<CropYear>) => {
    productAnalytics.track("soilsights_onboarding:practice_update", { practice: "rotation_table" });
    setChangeCropYears((previous) => previous.map((year) => (year.id === yearId ? { ...year, ...updates } : year)));
  };

  // Negative-forecast escape hatch (Figma): back to the generated recommendation.
  // Setting the rows re-triggers the on-change recalc automatically.
  const handleResetScenario = () => {
    setChangeCropYears(generated.initialChangeYears);
  };

  // No per-practice percentage: the calculation API has no practice-level attribution, so the only
  // honest number on this card is the total (FR3). Toggle + tooltip until the model provides one.
  // A switch that cannot move in either direction is shown locked rather than snapping back on click.
  const toggleItems = PRACTICE_KEYS.map((key) => {
    const field = FIELD_BY_PRACTICE[key];
    const isInert = isPracticeToggleInert(changeCropYears, generated.changeYears, cropYears, field);
    // A locked switch reflects the rows, so "nothing to recommend" doesn't read as applied.
    const enabled = isInert && changeCropYears.length > 0 ? isPracticeInRows(changeCropYears, field) : practiceToggles[key];
    return { key, enabled, isInert };
  });

  // Figma step-3 states: a negative trajectory gets the warning banner with the reset action;
  // an improvement potential below 5% gets a dismissible "close to optimised" banner.
  const isNegativeLift = liftClassification === "negative";
  const isLowLift = liftClassification === "low" || liftClassification === "noChange";
  const stateBanner = isNegativeLift
    ? { type: "warning" as const, text: t("scenarioAnalysis:onboarding.step3.negativeLiftBanner") }
    : isLowLift && !isLowLiftBannerDismissed
    ? { type: "success" as const, text: t("scenarioAnalysis:onboarding.step3.lowLiftBanner") }
    : null;

  return (
    <OnboardingLayout
      stepIndex={2}
      pageName="scenario-onboarding-step-3"
      title={t("scenarioAnalysis:onboarding.step3.heading")}
      subtitle={t("scenarioAnalysis:onboarding.step3.subheading")}
      footer={{
        onBack: () => navigate("/scenarios-onboarding/step-2"),
        ctaLabel: t("scenarioAnalysis:onboarding.step3.finishCta"),
        onCtaClick: handleFinish,
        ctaDisabled: isCompleting || isCalculating,
        ctaLoading: isCompleting,
        ctaTestId: "onboarding-finish-btn",
      }}
    >
      <div className="onboarding-step__content">
        <PracticeToggleCard items={toggleItems} totalUpliftPct={upliftPct} isCalculating={isCalculating || isCatalogueMissing} onToggle={handleToggle} />

        {stateBanner && !isCalculating && (
          <AlertBanner
            dataTestId="onboarding-step3-state-banner"
            type={stateBanner.type}
            iconName={isNegativeLift ? undefined : "tabler/confetti"}
            iconAlign="start"
            description={stateBanner.text}
            link={isNegativeLift}
            linkText={isNegativeLift ? t("scenarioAnalysis:onboarding.step3.resetCta") : undefined}
            onClick={isNegativeLift ? handleResetScenario : undefined}
            closeable={!isNegativeLift}
            onClose={!isNegativeLift ? () => setIsLowLiftBannerDismissed(true) : undefined}
            className="margin-bottom-150"
          />
        )}

        <div className="onboarding-table-card">
          <div className="onboarding-table-card__header">
            <span className="onboarding-scenario-card__dot onboarding-scenario-card__dot--blue" />
            <Typography variant="paragraph-m">{t("scenarioAnalysis:onboarding.adaptedCard.title")}</Typography>
          </div>
          <CropRotationTable
            variant="onboarding"
            years={changeCropYears}
            mainCropOptions={mainCropOptions}
            isMainCropOptionsLoading={isPracticeCropsFetching}
            onYearDataChange={handleYearDataChange}
            hideRefineAction
            hideDeleteAction
            disableCropSelection
            recommendedFieldsByYearId={highlightedFields}
            dataTestId="onboarding-change-table"
          />
        </div>
      </div>
    </OnboardingLayout>
  );
};
